import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import { useAuth } from '../../store/AuthContext'
import usersService from '../../services/usersService'
import { UserAdmin, CreateUserRequest, UpdateUserRequest, UserRole } from '../../services/types'
import { ConfirmModal } from '../../components/ConfirmModal'

interface UserFormData {
  username: string
  email: string
  first_name: string
  last_name: string
  role: UserRole
  password: string
  groups: number[]
  is_active: boolean
}

const emptyForm: UserFormData = {
  username: '',
  email: '',
  first_name: '',
  last_name: '',
  role: 'admin',
  password: '',
  groups: [],
  is_active: true,
}

const roleLabels: Record<UserRole, string> = {
  admin: 'Administrador',
  manager: 'Gestor',
  viewer: 'Colaborador',
}

const roleBadgeColors: Record<UserRole, string> = {
  admin: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
  manager: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  viewer: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
}

export const UsersPage = () => {
  const toast = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'admin'

  // State
  const [search, setSearch] = useState('')
  const [filterRole, setFilterRole] = useState<string>('')
  const [filterActive, setFilterActive] = useState<'' | 'true' | 'false'>('')
  const [showForm, setShowForm] = useState(false)
  const [editingUser, setEditingUser] = useState<UserAdmin | null>(null)
  const [formData, setFormData] = useState<UserFormData>(emptyForm)
  const [formError, setFormError] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<UserAdmin | null>(null)

  // Fetch users
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['admin-users', search, filterRole, filterActive],
    queryFn: () =>
      usersService.getAll({
        search: search || undefined,
        role: filterRole || undefined,
        is_active: filterActive === '' ? undefined : filterActive === 'true',
      }),
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateUserRequest) => usersService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      closeForm()
      toast.success('Usuário criado com sucesso!')
    },
    onError: (error: any) => {
      const data = error.response?.data
      const message =
        data?.detail ||
        data?.username?.[0] ||
        data?.email?.[0] ||
        data?.password?.[0] ||
        data?.non_field_errors?.[0] ||
        (typeof data === 'object' ? Object.values(data).flat().join(' ') : null) ||
        'Erro ao criar usuario.'
      setFormError(message)
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      usersService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      closeForm()
      toast.success('Usuário atualizado com sucesso!')
    },
    onError: (error: any) => {
      const data = error.response?.data
      const message =
        data?.detail ||
        data?.email?.[0] ||
        data?.username?.[0] ||
        (typeof data === 'object' ? Object.values(data).flat().join(' ') : null) ||
        'Erro ao atualizar usuario.'
      setFormError(message)
    },
  })

  // Toggle active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      usersService.update(id, { is_active }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Erro ao alterar status do usuario.')
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => usersService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setDeleteTarget(null)
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.detail ||
          'Erro ao excluir usuario. Verifique se nao ha dados vinculados.'
      )
      setDeleteTarget(null)
    },
  })

  // Form handlers
  const openCreate = () => {
    setEditingUser(null)
    setFormData(emptyForm)
    setFormError('')
    setShowForm(true)
  }

  const openEdit = (u: UserAdmin) => {
    setEditingUser(u)
    setFormData({
      username: u.username,
      email: u.email,
      first_name: u.first_name,
      last_name: u.last_name,
      role: u.role,
      password: '',
      groups: u.groups,
      is_active: u.is_active,
    })
    setFormError('')
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingUser(null)
    setFormData(emptyForm)
    setFormError('')
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setFormError('')

    if (!formData.username.trim()) {
      setFormError('Nome de usuario e obrigatorio.')
      return
    }
    if (!formData.email.trim()) {
      setFormError('Email e obrigatorio.')
      return
    }
    if (!editingUser && !formData.password) {
      setFormError('Senha e obrigatoria para novos usuarios.')
      return
    }
    if (!editingUser && formData.password.length < 8) {
      setFormError('A senha deve ter no minimo 8 caracteres.')
      return
    }

    if (editingUser) {
      const payload: UpdateUserRequest = {
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        is_active: formData.is_active,
      }
      updateMutation.mutate({ id: editingUser.id, data: payload })
    } else {
      const payload: CreateUserRequest = {
        username: formData.username.trim(),
        email: formData.email.trim(),
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        role: formData.role,
        password: formData.password,
        groups: undefined,
      }
      createMutation.mutate(payload)
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Usuarios
        </h1>
        {isAdmin && (
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors"
          >
            + Novo Administrador
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Buscar
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Nome, email ou usuario..."
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Role Filter */}
          <div className="md:w-48">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Perfil
            </label>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="admin">Administrador</option>
              <option value="manager">Gestor</option>
              <option value="viewer">Colaborador</option>
            </select>
          </div>

          {/* Status Filter */}
          <div className="md:w-40">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Status
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value as '' | 'true' | 'false')}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">Todos</option>
              <option value="true">Ativos</option>
              <option value="false">Inativos</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">
            {users.length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Admins</p>
          <p className="text-2xl font-bold text-red-600 dark:text-red-400">
            {users.filter((u) => u.role === 'admin').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Gestores</p>
          <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
            {users.filter((u) => u.role === 'manager').length}
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Colaboradores</p>
          <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
            {users.filter((u) => u.role === 'viewer').length}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Carregando usuarios...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-5xl mb-4">👤</div>
            <p className="text-gray-600 dark:text-gray-400">
              Nenhum usuario encontrado.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Perfil
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Grupos
                  </th>
                  <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  {isAdmin && (
                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Acoes
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                      !u.is_active ? 'opacity-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white">
                          @{u.username}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {u.first_name} {u.last_name} &middot; {u.email}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          roleBadgeColors[u.role]
                        }`}
                      >
                        {roleLabels[u.role]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {u.groups_detail.length > 0 ? (
                          u.groups_detail.map((g) => (
                            <span
                              key={g.id}
                              className="inline-flex px-2 py-0.5 rounded text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400"
                            >
                              {g.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Nenhum</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => {
                          if (isAdmin && u.id !== user?.id) {
                            toggleActiveMutation.mutate({
                              id: u.id,
                              is_active: !u.is_active,
                            })
                          }
                        }}
                        disabled={!isAdmin || u.id === user?.id || toggleActiveMutation.isPending}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                          !isAdmin || u.id === user?.id ? 'cursor-default' : 'cursor-pointer'
                        } ${
                          u.is_active
                            ? 'bg-green-500'
                            : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                        title={
                          u.id === user?.id
                            ? 'Voce nao pode desativar seu proprio usuario'
                            : u.is_active
                            ? 'Ativo - clique para desativar'
                            : 'Inativo - clique para reativar'
                        }
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                            u.is_active ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => openEdit(u)}
                            className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                          >
                            Editar
                          </button>
                          {u.id !== user?.id && (
                            <button
                              onClick={() => setDeleteTarget(u)}
                              className="px-3 py-1 text-sm border border-danger rounded-btn text-danger hover:bg-danger hover:text-white transition-colors"
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={closeForm} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-gray-800 rounded-card shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                {editingUser ? 'Editar Usuario' : 'Novo Administrador'}
              </h3>

              {formError && (
                <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-btn text-danger text-sm">
                  {formError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Username */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nome de Usuario *
                  </label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) =>
                      setFormData({ ...formData, username: e.target.value.toLowerCase().replace(/\s/g, '.') })
                    }
                    disabled={!!editingUser}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent font-mono disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="nome.sobrenome"
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="email@exemplo.com"
                    required
                  />
                </div>

                {/* First Name / Last Name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nome
                    </label>
                    <input
                      type="text"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Nome"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sobrenome
                    </label>
                    <input
                      type="text"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Sobrenome"
                    />
                  </div>
                </div>

                {/* Role (only on edit, locked for admins) */}
                {editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Perfil de Acesso *
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                      disabled={editingUser.role === 'admin'}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <option value="viewer">Colaborador</option>
                      <option value="manager">Gestor</option>
                      <option value="admin">Administrador</option>
                    </select>
                    {editingUser.role === 'admin' && (
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        O perfil de um administrador nao pode ser rebaixado.
                      </p>
                    )}
                  </div>
                )}

                {/* Password (only on create) */}
                {!editingUser && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Senha *
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Minimo 8 caracteres"
                      minLength={8}
                      required
                    />
                  </div>
                )}

                {/* Active toggle (only on edit) */}
                {editingUser && (
                  <div>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData({ ...formData, is_active: e.target.checked })
                        }
                        disabled={editingUser.id === user?.id}
                        className="rounded border-gray-300 text-primary focus:ring-primary w-5 h-5"
                      />
                      <div>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Usuario ativo
                        </span>
                        {editingUser.id === user?.id && (
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            Voce nao pode desativar seu proprio usuario.
                          </p>
                        )}
                      </div>
                    </label>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={closeForm}
                    disabled={isSaving}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving
                      ? 'Salvando...'
                      : editingUser
                      ? 'Salvar Alteracoes'
                      : 'Criar Administrador'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Excluir Usuario"
        description={
          <p>
            Tem certeza que deseja excluir o usuario <strong>@{deleteTarget?.username}</strong>?
            Esta acao nao pode ser desfeita.
          </p>
        }
        confirmText="Sim, Excluir"
        cancelText="Cancelar"
        isDanger
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export default UsersPage
